import { extractImageSource, imageHoverTransition } from 'utils/imageUtils';
import { transformImages } from 'products/transformProductImages';
import { DesignSourceEnum } from '@insights/insights-data-provider';
import PriceModule from 'ui/PriceModule/PriceModule';
import { PriceModuleLayoutTypeEnum } from '@inter-ikea-kompis/component-price-module';
import styled from 'styled-components';
import { useServiceSettings } from 'contexts/serviceSettings';
import React, { useEffect, useState } from 'react';
import { GalleryGridConfig, GalleryItemConfig, Spr2VpcMap } from 'rangeConfig/@types';
import { getCountry } from 'i18n/i18nUtils';
import {
  ImageType,
  ProductImageDetails,
  fetchDEXFProductsForItemList
} from '@inter-ikea-storageone/range';
import { e2e, getRange } from 'utils/queryUtils';
import { Link, useHistory } from 'react-router-dom';
import { getPointerEventsValue } from 'rangeConfig/shared/utils/getPointerEventsValue';
import { IShoppingProduct } from '@inter-ikea-kompis/types';
import Body from 'ui/Typography/Body';
import { getIProductDimensionsForNarration } from 'utils/measurementUtils';
import { useIntl } from 'react-intl';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { isMobile } from 'utils/platform';
import Title from 'ui/Typography/Title';
import Carousel from '@ingka/carousel';

import '@ingka/svg-icon/dist/style.css';
import '@ingka/button/dist/style.css';
import '@ingka/carousel/dist/style.css';
import '@ingka/focus/dist/style.css';
import Card from '@ingka/card';
import { AppRoutes } from 'routes';
import { reportLandingPageSelectStart } from 'analytics/reporters';
import Pill from 'ui/Pill/Pill';
import { useDispatch, useSelector } from 'react-redux';
import { getAgeGroup, setAgeGroup } from 'ducks/layout';
import { usePlannerConfig } from 'contexts/plannerConfig';
import { useTranslations } from 'hooks/useTranslations';

type Item = {
  shoppingProduct: IShoppingProduct;
  vpcCode: string;
  image?: { index: number; type: ImageType };
  dataTestId?: string;
};

const CarouselWrapper = styled.div`
  max-width: 100%;
  margin-top: ${({ theme }) => theme.space.xxl};
  margin-bottom: ${({ theme }) => theme.space.l};
`;

const StyledCard = styled(Card)`
  .card__info {
    justify-content: space-between;
  }
`;

const Solution = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: auto;
  font-size: 1.375rem;
  padding-top: 0.2rem;
  width: 200px;
`;

const Cardwrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 200px;
  margin-top: 0.2rem;
  height: 100%;
  background-color: #d4c6af;
`;

const LinkWrapper = styled(Link)`
  text-decoration: none;

  &:focus-visible {
    transition: all 0.2s ease-in-out;
    outline: 0.125rem solid ${({ theme }) => theme.colors.feedback.focus};

    p {
      outline: none !important;
    }
  }
`;

const ImageWrapper = styled.div`
  height: 200px;
  width: 200px;
  position: relative;
  overflow: hidden;
  pointer-events: ${getPointerEventsValue()};
`;

const SolutionImage = styled.img`
  object-fit: cover;
  width: 100%;
  height: 100%;
  transition: ${imageHoverTransition()};

  :hover {
    transform: scale(1.075);
  }
`;

const PillWrapper = styled.div`
  margin-bottom: 0;
`;

const getGalleryImages = (
  images: ProductImageDetails[],
  desiredImage?: { index: number; type: ImageType }
): ProductImageDetails[] => {
  if (desiredImage) {
    const desiredImagesInDesiredFormat = images.filter(image => image.type === desiredImage.type);

    if (desiredImagesInDesiredFormat.length > desiredImage.index) {
      return [desiredImagesInDesiredFormat[desiredImage.index]];
    }
  }

  const contextImages = images.filter(image => image.type === ImageType.CPP);
  if (contextImages.length > 0) {
    return contextImages;
  }
  const fallbackImages = images.filter(image => image.type === ImageType.IPP);
  if (fallbackImages.length > 0) {
    return fallbackImages;
  }
  const fallbackFallbackImages = images.filter(image => image.type === ImageType.NSPP);
  return fallbackFallbackImages;
};

const getVpc = (item: GalleryItemConfig, sprToVpcMap: Spr2VpcMap) => {
  const sprId = item?.product?.itemId.replace('SPR-', '');
  if (!sprId) return null;

  return sprToVpcMap[sprId];
};

const GuidaItem = () => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const onGuideClick = () => {
    history.push(AppRoutes.GUIDE);
    reportLandingPageSelectStart({ start: 'guide_me' });
  };

  return (
    <Cardwrapper key={'guide-id'}>
      <StyledCard
        title={formatMessage({ id: 'storageone.landingPageGuideTitle' })}
        body={formatMessage({
          id: 'storageone.landingPageGuideDescription'
        })}
        data-testid="item-guide"
        emphasised
        ctaLabel={formatMessage({
          id: 'storageone.landingPageBtnGuide'
        })}
        onClick={onGuideClick}
        onKeyUp={e => e.key === 'Enter' && onGuideClick()}
        buttonVariant="icon-primary-inverse"
        compact={!isMobile()}
        tabIndex={0}
      />
    </Cardwrapper>
  );
};

type Props = {
  item: Item;
  index: number;
};

const GalleryItem = ({ item, index }: Props) => {
  const { shoppingProduct, vpcCode } = item;
  const images = shoppingProduct?.product?.content?.image
    ? transformImages(shoppingProduct?.product?.content?.image)
    : [];
  const { unit, useMetric } = useTranslations();

  const galleryImages = getGalleryImages(images, item.image);
  const contextImages = galleryImages.length > 0 ? galleryImages : images;

  if (contextImages.length === 0) {
    return null;
  }

  const { srcset, fallbackImage } = extractImageSource(contextImages.flatMap(s => s.sources));

  const getSolutionDescription = (productContent?: any): string | undefined => {
    return `${productContent?.name}, ${productContent?.typeName}, ${productContent?.validDesignText}`;
  };

  const measurements = useMetric
    ? shoppingProduct?.product?.content?.measureReference?.textMetric
    : shoppingProduct?.product?.content?.measureReference?.textImperial;

  const fontType = isMobile()
    ? ThemeFontStyleTypeEnum.bodyMedium
    : ThemeFontStyleTypeEnum.bodyLarge;

  const to = `/planner?vpc=${vpcCode}&vpcSource=${DesignSourceEnum.gallery}`;

  return (
    <Solution key={shoppingProduct.product.itemId} data-testid={`solution${index}`}>
      <LinkWrapper to={to}>
        <ImageWrapper>
          <SolutionImage
            srcSet={srcset}
            src={fallbackImage.src}
            alt={getSolutionDescription(shoppingProduct.product.content)}
            sizes="400px"
          />
        </ImageWrapper>
        <Body
          type={fontType}
          aria-label={
            shoppingProduct.product.content?.measure
              ? getIProductDimensionsForNarration(shoppingProduct.product, unit, useMetric)
              : ''
          }
        >
          {measurements}
        </Body>
        <PriceModule
          shoppingProducts={[shoppingProduct]}
          layout={PriceModuleLayoutTypeEnum.default}
        />
      </LinkWrapper>
    </Solution>
  );
};

const FilterGalleryItems = (
  galleryConfig: GalleryGridConfig[],
  spr2VpcMap: Spr2VpcMap,
  ageGroup: string,
  range: string
) => {
  const filterItems =
    range === 'smastad' && ageGroup
      ? galleryConfig.filter(item => item.ageGroup === ageGroup)
      : galleryConfig;

  const vpcCodesInGallery: string[] = [];

  const items = filterItems?.[0]?.items
    .filter(p => p.product && p.product.valid)
    .flatMap((item: GalleryItemConfig) => {
      const vpcCode = getVpc(item, spr2VpcMap);
      if (!item.product || !vpcCode || vpcCodesInGallery.includes(vpcCode)) {
        return [];
      }
      vpcCodesInGallery.push(vpcCode);
      return {
        vpcCode,
        shoppingProduct: {
          quantity: 1,
          product: item.product
        },
        image: item.spr.image
      };
    });
  return items;
};

const Gallery = (items: Item[]) => {
  const galleryArray = items.map((item: Item, index: number) => {
    return {
      component: <GalleryItem item={item} index={index} />,
      key: item.shoppingProduct.product.itemId
    };
  });
  return [...galleryArray];
};

const LandingPageGallery = () => {
  const { kompisSettings } = useServiceSettings();
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const range = getRange();
  const ageGroup = useSelector(getAgeGroup);
  const [galleryConfig, setGalleryConfig] = useState<GalleryGridConfig[]>([]);
  const {
    plannerConfig: { landingPageConfig, sprToVpcMap }
  } = usePlannerConfig();

  useEffect(() => {
    const getProductData = async () => {
      const country = getCountry();
      const plannerGalleryConfig =
        landingPageConfig.browseInspiration.galleryConfig[country] ||
        landingPageConfig.browseInspiration.galleryConfig.default;
      const accumulatedConfigs: GalleryGridConfig[] = [];

      for (const config of plannerGalleryConfig) {
        await fetchDEXFProductsForItemList(
          kompisSettings,
          config.sprs.map(s => s.id)
        ).then(dexfData => {
          const galleryItems = config.sprs.map(spr => {
            const dexf = dexfData.find(d => spr.id === d.itemId);
            return { spr, product: dexf, ageGroup: config.ageGroup };
          });

          accumulatedConfigs.push({
            items: galleryItems,
            ageGroup: config.ageGroup,
            title: config.title
          });
        });
      }
      setGalleryConfig(accumulatedConfigs);
    };

    getProductData();
  }, [range, kompisSettings, landingPageConfig.browseInspiration.galleryConfig]);

  const galleryItems: Item[] = FilterGalleryItems(galleryConfig, sprToVpcMap, ageGroup, range);

  if (!galleryItems || galleryItems?.length < 1) return null;

  const initialGallery = Gallery(galleryItems);
  const galleryWithGuida = [...initialGallery];
  galleryWithGuida.splice(1, 0, { component: <GuidaItem />, key: 'guide-id' } as any);

  return (
    <CarouselWrapper>
      <Title pb="s" type={ThemeFontStyleTypeEnum.headingMedium}>
        {formatMessage({ id: 'storageone.landingPageGalleryTitle' })}
      </Title>
      {range === 'smastad' && (
        <PillWrapper>
          <Carousel id="gallery-carousel" hasNoControls={!e2e()}>
            {galleryConfig.map(
              config =>
                config.title && (
                  <Pill
                    label={formatMessage({ id: config.title })}
                    key={`age-group-${config.ageGroup}`}
                    data-testid={`age-group-${config.ageGroup}`}
                    onClick={() => config.ageGroup && dispatch(setAgeGroup(config.ageGroup))}
                    selected={config.ageGroup === ageGroup}
                  />
                )
            )}
          </Carousel>
        </PillWrapper>
      )}
      <Carousel
        id="landingpage-carousel"
        className="gallery"
        hasNoControls={e2e()}
        data-testid="inspiration-gallery"
      >
        {range === 'pax' && !isMobile()
          ? galleryWithGuida.map(item => {
              return <React.Fragment key={item.key}>{item.component}</React.Fragment>;
            })
          : galleryItems?.map((item: Item, index: number) => {
              return <GalleryItem key={index} item={item} index={index} />;
            })}
      </Carousel>
    </CarouselWrapper>
  );
};

export default LandingPageGallery;
